// Safari 套餐体系数据源
// 内容来源：三份供应商 PDF 核对压缩后整理，仅作为参考方案展示。
// 注意：每日行程配图采用 AI 生成的目的地/活动风景示意（非真实酒店/营地宣传图），
// 住宿区继续使用占位容器，后续上传自有或取得书面媒体授权的素材后再替换。

export interface PriceRow {
  date: string;
  price: string;
}

export interface PriceTable {
  name: string;
  rows: PriceRow[];
  note?: string;
}

export interface RouteVersionOption {
  name: string;
  detail: string;
  prices?: PriceRow[];
}

export interface RouteVersion {
  title: string;
  intro?: string;
  options: RouteVersionOption[];
}

export interface ItineraryActivity {
  name: string;
  icon: string;
}

export interface ItineraryDay {
  day: string;
  content: string;
  title?: string;
  images?: string[];
  imageAlts?: string[];
  activities?: ItineraryActivity[];
  location?: string;
  arrangement?: string;
  accommodation?: string;
  meals?: string;
}

export interface MapStop {
  name: string;
  dayLabel: string;
  coords: [number, number];
}

export interface Camp {
  name: string;
  nights: string;
  detail: string;
  suggestedImage: string;
  image?: string;
  gallery?: string[];
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface Highlight {
  title: string;
  description: string;
  icon: string;
}

export interface SafariPackageDetail {
  path: string;
  slug: string;
  name: string;
  h1: string;
  heroSubtitle: string;
  seoTitle: string;
  seoDescription: string;
  keywords: string;
  tags: string[];
  overview: { label: string; value: string }[];
  routeText: string;
  features: string[];
  highlights: Highlight[];
  plannerNote: string;
  itinerary?: ItineraryDay[];
  camps?: Camp[];
  campFacts?: string[];
  routeVersions: RouteVersion[];
  priceIntro?: string;
  priceTables?: PriceTable[];
  consultationPriceNote?: string;
  ctaTitle: string;
  ctaButton: string;
  duration?: string;
  priceRange?: string;
  minPrice?: string;
  heroImage?: string;
  mapStops?: MapStop[];
}

export interface SafariPackageCard {
  path: string;
  name: string;
  country: string;
  parks: string;
  duration: string;
  camps: string;
  vehicle: string;
  price: string;
}

// ---------- 总览页卡片 ----------
export const safariPackageCards: SafariPackageCard[] = [
  {
    path: '/safari-packages/tanzania/2027-all-asilia-northern-circuit',
    name: '2027 All Asilia 坦桑尼亚北部野奢环线',
    country: '坦桑尼亚',
    parks: '塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂或恩杜图',
    duration: '8天7晚',
    camps: 'Oliver\'s Camp + The Highlands + Sayari / Namiri Plains / Olakira 三选一',
    vehicle: '共享车辆游猎',
    price: '参考价（分季）',
  },
  {
    path: '/safari-packages/tanzania/2027-lemala-safari',
    name: '2027 坦桑尼亚 Lemala 野奢 Safari',
    country: '坦桑尼亚',
    parks: '塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂',
    duration: '8天7晚',
    camps: 'Mpingo Ridge + Osonjoi + Nanyukie / Kuria Hills 二选一',
    vehicle: '共享车辆和向导',
    price: '参考价（分季）',
  },
  {
    path: '/safari-packages/east-africa/2026-andbeyond-safari',
    name: '2026 东非 8天7晚 野奢游猎',
    country: '肯尼亚 + 坦桑尼亚连线',
    parks: '马赛马拉、塞伦盖蒂、恩戈罗恩戈罗火山口',
    duration: '8天7晚',
    camps: '以 andBeyond 营地为核心，Bateleur + 塞伦盖蒂三选一 + 火山口二选一',
    vehicle: '车辆和向导以最终确认单为准',
    price: '请咨询',
  },
];

// ---------- 共享：费用包含 / 不含 / 价格提示 / 预订提示 / 运营主体 / FAQ ----------
export const safariIncludeItems = [
  '列明的 7 晚双人住宿',
  '住宿期间三餐与当地酒水',
  '营地和附近简易机场接送',
  '洗衣',
  '列明的 Safari 活动',
  '公园 / 保护区门票',
  '列明的内陆航班及税费',
  '参考方案中写明的紧急救援保障',
];

export const safariIncludeNote =
  'All Asilia 与 Lemala 明确为共享车辆；东非组合的车辆和向导需书面确认。';

export const safariExcludeItems = [
  '往返东非的国际机票及税费',
  '签证或 ETA',
  '当地小费',
  '旅行意外险',
  '个人消费',
  '热气球、直升机、按摩 / 水疗、部分文化和私人活动',
  '未列明费用',
];

export const safariExcludeNote =
  '紧急救援保障不等同旅行意外险，保险由客人自行购买。';

export const safariPriceNotice =
  '页面价格用于理解不同季节和路线版本的预算差异，不代表实时房态或最终成交价。具体住宿、内陆航班、活动、税费与价格以咨询后出具的最终确认方案和合同为准。';

export const safariBookingTips = [
  '共享车辆不是私人包车。',
  '动物迁徙、渡河、黑犀牛和大猫均不能保证。',
  '小飞机行李重量、软包尺寸和航班时刻需按航司确认。',
  '儿童价、最低年龄、家庭房、加床、单房差、付款、取消及改期条款需逐项确认。',
  '4月关闭营地不能原样安排。',
  '跨国路线通常是开口行程。',
];

export const safariLegalNotice =
  '成都自由旷野国际旅行社有限公司持有旅行社业务经营许可证，许可证编号为L-SC-100448。具体许可经营范围以许可证及主管部门公示信息为准。';

export const safariCommonFaq: FaqItem[] = [
  {
    question: '页面上的参考价格就是最终成交价吗？',
    answer:
      '页面价格用于理解不同季节和路线版本的预算差异，不代表实时房态或最终成交价。具体住宿、内陆航班、活动、税费与价格以咨询后出具的最终确认方案和合同为准。',
  },
  {
    question: '这些行程包含国际机票吗？',
    answer: '不含往返东非的国际机票及税费，签证或 ETA 也需自行办理。',
  },
  {
    question: '能保证看到动物迁徙、渡河、黑犀牛或大猫吗？',
    answer: '动物迁徙、渡河、黑犀牛和大猫均不能保证，均属于自然现象，无法承诺。',
  },
  {
    question: 'Safari 车辆是私人包车吗？',
    answer:
      'All Asilia 与 Lemala 套餐明确为共享车辆，并非私人包车；东非组合方案的车辆和向导需以最终书面确认单为准。',
  },
  {
    question: '4月关闭的营地可以安排吗？',
    answer:
      '4月关闭的营地（如 Lake Manyara Tree Lodge、Grumeti River Lodge）不能原样安排，需按实际出行日期重新匹配。',
  },
  {
    question: '跨国路线怎么走？',
    answer: '跨国路线通常是开口行程，跨境航班与衔接以最终确认为准。',
  },
];

// ---------- 套餐一：All Asilia ----------
const allAsilia: SafariPackageDetail = {
  path: '/safari-packages/tanzania/2027-all-asilia-northern-circuit',
  slug: '2027-all-asilia-northern-circuit',
  name: '2027 All Asilia 坦桑尼亚北部野奢环线',
  duration: '8天7晚',
  priceRange: 'USD 7,115 /人起',
  h1: '2027 All Asilia 坦桑尼亚北部野奢环线｜8天7晚',
  heroImage: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/b164c5bc-d9b0-4adc-a63f-42f332400fd7_compressed_fw-safari-asilia-2027-hero-serengeti-migration.webp',
  heroSubtitle:
    '从塔兰吉雷、恩戈罗恩戈罗到塞伦盖蒂或恩杜图，全程 All Asilia 营地的坦桑尼亚北部野奢环线参考方案。',
  seoTitle: '2027坦桑尼亚All Asilia野奢Safari 8天7晚｜自由旷野',
  seoDescription:
    '2027坦桑尼亚北部8天7晚Safari参考行程，从塔兰吉雷Oliver\'s Camp、恩戈罗恩戈罗The Highlands前往塞伦盖蒂或恩杜图，可选Sayari、Namiri Plains或按季节迁营的Olakira，包含内陆航班、营地住宿、共享游猎与分季参考价格。',
  keywords: 'All Asilia,坦桑尼亚Safari,塔兰吉雷,恩戈罗恩戈罗,塞伦盖蒂,Olakira,Sayari,Namiri Plains,野奢营地,8天7晚',
  tags: ['坦桑尼亚', '8天7晚', '乞力马扎罗机场往返', '2人起', '2027参考价', '不含国际机票'],
  overview: [
    { label: '目的地', value: '坦桑尼亚' },
    { label: '行程天数', value: '8天7晚' },
    { label: '进出机场', value: '乞力马扎罗机场往返' },
    { label: '出行人数', value: '2人起' },
    { label: '保护区', value: '塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂或恩杜图' },
    { label: '营地组合', value: 'Oliver\'s Camp 2晚 + The Highlands 2晚 + 第三段营地3晚' },
    { label: '交通方式', value: '内陆小飞机 + 营地接送' },
    { label: '游猎方式', value: '共享车辆游猎' },
    { label: '参考价格', value: '2027参考价' },
    { label: '不含项', value: '国际机票' },
  ],
  routeText:
    '乞力马扎罗机场 → 塔兰吉雷2晚 → 恩戈罗恩戈罗高地2晚 → 塞伦盖蒂或恩杜图第三段营地三选一3晚 → 乞力马扎罗机场',
  features: [
    '全程 All Asilia 旗下营地，塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂或恩杜图三大区域串联',
    '内陆小飞机衔接 + 营地接送，减少长途车程',
    '第三段营地按季节三选一：Sayari、Namiri Plains 或迁营的 Olakira',
    '共享车辆游猎，分季参考价格清晰呈现预算差异',
  ],
  highlights: [
    { title: '由南向北的节奏', description: '塔兰吉雷、火山口高地、塞伦盖蒂依次推进，每个区域连住两晚，不赶路。', icon: 'ri-route-line' },
    { title: '季节适配的第三段', description: '第三段营地按季节三选一，迁徙季选 Sayari 或 Olakira，大猫季选 Namiri Plains。', icon: 'ri-calendar-check-line' },
    { title: '连续住宿减少换营', description: '全程三个区域各住两晚以上，避免频繁收行李、反复转场。', icon: 'ri-hotel-line' },
    { title: '内陆小飞机衔接', description: '主要长距离移动由内陆小飞机完成，配合营地接送，减少长途车程。', icon: 'ri-plane-line' },
    { title: '三种营地版本可选', description: '第三段营地的位置与价格不同，预算与体验重点可自行取舍。', icon: 'ri-stack-line' },
  ],
  plannerNote:
    '这条环线遵循“由南向北、由易到难”的节奏：先在猴面包树下的塔兰吉雷适应落地节奏，再登上海拔较高的火山口高地，最后把最开阔、最不可预知的塞伦盖蒂留到行程后半段。三个区域各住两晚，避免频繁换营；第三段营地按季节三选一，是为了把迁徙季和大猫季分别交给最合适的营地。全程以内陆小飞机衔接，减少长途车程，适合第一次进入东非、希望在有限天数内走完经典北部环线的旅行者。',
  mapStops: [
    { name: '乞力马扎罗机场', dayLabel: 'D1', coords: [-3.429, 37.074] },
    { name: '塔兰吉雷国家公园', dayLabel: 'D1-2', coords: [-3.6, 36.0] },
    { name: '恩戈罗恩戈罗保护区', dayLabel: 'D3-4', coords: [-3.2, 35.55] },
    { name: '塞伦盖蒂国家公园', dayLabel: 'D5-7', coords: [-2.3, 34.8] },
    { name: '乞力马扎罗机场', dayLabel: 'D8', coords: [-3.429, 37.074] },
  ],
  itinerary: [
    {
      day: '第1–2天',
      title: '落地塔兰吉雷，住进猴面包树下的营地',
      location: '落地塔兰吉雷国家公园',
      arrangement: '每日游猎出车，追踪象群与狮群',
      accommodation: 'Oliver\'s Camp（2晚）',
      meals: '营地早餐 / 游猎野餐午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/525252d6-e904-4ce8-8a38-3be3d3310cbb_compressed_fw-safari-asilia-2027-d01-02-tarangire-road.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/0bb8b60e-d59e-4e3f-9ac5-2ccd24ec0e1a_compressed_fw-safari-asilia-2027-d01-02-tarangire-elephants.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/4e9793a5-d737-4be9-a605-97ac991e2716_compressed_fw-safari-asilia-2027-d01-02-olivers-game-drive.webp',
      ],
      imageAlts: ['塔兰吉雷国家公园道路', '塔兰吉雷象群', 'Oliver’s Camp 游猎车程'],
      content:
        '乘内陆小飞机降落塔兰吉雷，这片布满猴面包树的土地是非洲大象密度最高的国家公园之一。刚下飞机，你可能就会看见一队大象慢悠悠穿过跑道旁的灌木丛。两天两夜里，Oliver\'s Camp 的向导每天带你出车：晨雾中追踪狮群，黄昏时跟着象群走向水源，夜幕降临后草原切换成另一张面孔。如果想在夜色里走更深，营地提供夜间游猎（另订）；更想用热气球俯瞰猴面包树林的，也可以提前预约（另订）。',
      activities: [
        { name: '乞力马扎罗机场 接机 + 内陆航班', icon: 'ri-flight-takeoff-line' },
        { name: '每日游猎出车', icon: 'ri-compass-3-line' },
        { name: '夜间游猎（另订）', icon: 'ri-moon-clear-line' },
        { name: '热气球俯瞰（另订）', icon: 'ri-sun-line' },
      ],
    },
    {
      day: '第3天',
      title: '飞抵曼雅拉，翻过山脊登上火山口高地',
      location: '飞抵曼雅拉，转越野车登火山口高地',
      arrangement: '内陆航班 + 越野车转场约3小时',
      accommodation: 'The Highlands',
      meals: '营地早餐 / 营地午餐或野餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/dce32d61-1fd5-4142-a028-9aa81cd422ab_compressed_fw-safari-asilia-2027-d03-highlands-aerial.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/4f60ce87-8916-49eb-ad75-ecafdf23c5df_compressed_fw-safari-asilia-2027-d03-highlands-bedroom-view.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/25eec2d8-34a5-4124-b918-238f6d2404d9_compressed_fw-safari-asilia-2027-d03-highlands-walk.webp',
      ],
      imageAlts: ['曼雅拉—高地航拍', 'The Highlands 客房景观', '高地徒步步道'],
      content:
        '早餐后乘小飞机飞往曼雅拉机场，换乘越野车沿盘山路缓缓攀升。随着海拔升高，气温骤降，云雾开始在车窗外翻涌，地景从干燥稀树草原切换成翠绿的高原草甸。The Highlands 就坐落在 Olmoti 火山口边缘，推开帐篷门便是连绵起伏的高原景色——这是整段行程最安静、最像"逃离世界"的一夜。如果对马赛社区文化感兴趣，参访活动可在预订时与营地确认是否包含。',
      activities: [
        { name: '内陆航班', icon: 'ri-plane-line' },
        { name: '越野车转场（约3小时）', icon: 'ri-car-line' },
        { name: '马赛社区参访（需确认是否含）', icon: 'ri-community-line' },
      ],
    },
    {
      day: '第4天',
      title: '下行世界最大火山口，有机会集齐非洲五霸',
      location: '下行恩戈罗恩戈罗火山口',
      arrangement: '火山口全天游猎 + 底部野餐午餐',
      accommodation: 'The Highlands（2晚）',
      meals: '营地早餐 / 火山口底部野餐午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6ded9695-c980-4159-b60f-871e7ff0e27c_compressed_fw-safari-asilia-2027-d04-ngorongoro-crater-view.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/7a84fbfa-5254-4c97-b3f1-7e9f935329fe_compressed_fw-safari-east-africa-2026-d06-07-ngorongoro-rhino.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/3b98ecb2-95fe-4b90-b28a-4fde8a011576_compressed_fw-safari-asilia-2027-d04-ngorongoro-flamingos.webp',
      ],
      imageAlts: ['恩戈罗恩戈罗火山口', '恩戈罗恩戈罗狮子', '恩戈罗恩戈罗火烈鸟'],
      content:
        '驾车下行进入恩戈罗恩戈罗火山口——260 平方公里的封闭盆地，宛如一座天然动物庄园：狮群、非洲象、非洲水牛、花豹在同一片草原同框出现，而这里也是非洲最稳定的黑犀牛栖息地之一。运气好时，你可以在清晨薄雾中看到那抹稀少的黑色轮廓，离集齐非洲五霸只差这临门一脚。所有动物均为野生，无法承诺出现。午餐在火山口底部野餐享用，四周是宽阔草原和浅蓝色苏打湖。意犹未尽？Olmoti 火山口徒步（另订）可让你傍晚步行探索另一座独立火山口，提前与营地确认。',
      activities: [
        { name: '火山口全天游猎', icon: 'ri-landscape-line' },
        { name: '火山口底部野餐午餐', icon: 'ri-restaurant-2-line' },
        { name: 'Olmoti 火山口徒步（另订）', icon: 'ri-footprint-line' },
        { name: '马赛文化体验（另订）', icon: 'ri-community-line' },
      ],
    },
    {
      day: '第5天',
      title: '空中飞越塞伦盖蒂，进入“无边的平原”',
      location: '飞抵塞伦盖蒂',
      arrangement: '内陆航班 + 傍晚出车游猎',
      accommodation: '第三段营地（三选一）',
      meals: '营地早餐 / 游猎野餐午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/01372b33-0fde-46ba-a210-217b638ea8a1_compressed_fw-safari-asilia-2027-d05-serengeti-sunset.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/3c647e50-cb03-489a-90f3-0787b0bf8d2f_compressed_fw-safari-asilia-2027-d05-sayari-landscape.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/16b09350-3310-45ce-9bec-b4fbda277a02_compressed_fw-safari-asilia-2027-d05-namiri-exterior.webp',
      ],
      imageAlts: ['塞伦盖蒂日落', 'Sayari 草原景观', 'Namiri Plains 营地外观'],
      content:
        '乘小飞机越过高地，在无边草海上空俯瞰塞伦盖蒂——从机窗已能看见地面上移动的黑色兽群。抵达营地稍作安顿，傍晚向导便带着你驶向草原：换营第一次出车，往往是整段行程最令人屏气的时刻，你永远不知道转过那片丘陵会撞上什么。',
      activities: [
        { name: '内陆航班', icon: 'ri-plane-line' },
        { name: '营地接送', icon: 'ri-car-line' },
        { name: '傍晚出车游猎', icon: 'ri-compass-3-line' },
      ],
    },
    {
      day: '第6–7天',
      title: '两整天扎进塞伦盖蒂，追踪大猫与兽群',
      location: '塞伦盖蒂两整天游猎',
      arrangement: '每日两次出车，追踪大猫与兽群',
      accommodation: '第三段营地（3晚）',
      meals: '营地早餐 / 游猎野餐午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/9dbd60cb-4be1-45cd-a472-edfeb403c4da_compressed_fw-safari-asilia-2027-d06-07-serengeti-cheetahs.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/0a9e1db2-4fd1-4528-b2cf-3e1c757aaecd_compressed_fw-safari-asilia-2027-d06-07-sayari-river-crossing.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/e0e56ab8-bf83-4c88-aaf5-44ce5fca3ebb_compressed_fw-safari-asilia-2027-d06-07-olakira-river-crossing.webp',
      ],
      imageAlts: ['塞伦盖蒂猎豹', 'Sayari 渡河', 'Olakira 渡河'],
      content:
        '早出晚归，每天两次出车，把时间全部交给草原。选 Sayari：马拉河渡口近在咫尺，雨季结束后兽群北上，角马在河边积聚、跃入、挣扎的场面就发生在视线范围内——当然，渡河是自然现象，无法保证。选 Namiri Plains：东部草原以大猫密集著称，猎豹母子、伏击中的狮群频繁出没，这是整个塞伦盖蒂中最适合"大猫控"的区域。选 Olakira：营地随季节迁移，6–10 月在北部追踪迁徙，12–3 月在恩杜图守候角马产仔，节奏不同，画面截然不同。如果还想再多些体验，专属摄影车、丛林徒步、热气球日出均可与营地另行预约（另订）。',
      activities: [
        { name: '每日两次游猎出车', icon: 'ri-compass-3-line' },
        { name: '专属摄影车（另订）', icon: 'ri-camera-line' },
        { name: '丛林徒步 Safari（另订）', icon: 'ri-footprint-line' },
        { name: '热气球日出俯瞰（另订）', icon: 'ri-sun-line' },
      ],
    },
    {
      day: '第8天',
      title: '最后一个草原清晨，飞回乞力马扎罗',
      location: '飞回乞力马扎罗机场',
      arrangement: '内陆航班返程，衔接国际航班',
      accommodation: '—',
      meals: '营地早餐 / 游猎野餐午餐盒或机场简餐 / 飞机上自理',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/1191ee70-55cb-47dc-8568-2567ddede28f_compressed_fw-safari-asilia-2027-d08-serengeti-sunset-vehicle.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/3cfc0bf4-38e4-4d28-bf57-9f2955e41c01_compressed_fw-safari-asilia-2027-d08-sayari-sundown.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5c5733fd-3460-4fec-906a-a8fb3d2670b5_compressed_fw-safari-asilia-2027-d08-namiri-landscape.webp',
      ],
      imageAlts: ['塞伦盖蒂日落游猎车', 'Sayari 日落', 'Namiri 草原景观'],
      content:
        '清晨最后一次望向营地外的草原——阳光刚拉开帷幕，远处可能还有象群剪影。搭内陆小飞机飞回乞力马扎罗机场，为国际航班预留足够的衔接时间。行程到这里结束，但关于这片旷野的记忆，才刚刚开始消化。',
      activities: [
        { name: '晨间出车（视时间）', icon: 'ri-compass-3-line' },
        { name: '内陆航班返程', icon: 'ri-plane-line' },
        { name: '国际航班衔接', icon: 'ri-flight-takeoff-line' },
      ],
    },
  ],
  camps: [
    {
      name: 'Oliver\'s Camp',
      nights: '2晚',
      detail: '塔兰吉雷南部，邻近Kuro。',
      suggestedImage: 'camp-olivers-camp.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/9574e509-c0f0-499d-85d9-63539a576b53_compressed_fw-safari-asilia-2027-stay-olivers-bedroom.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/c9a8a289-9093-40c3-a7e7-16e5d419a8de_compressed_fw-safari-asilia-2027-stay-olivers-exterior.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/f6a7ea6f-07bf-4b77-9d49-f9ee38d2bf83_compressed_fw-safari-asilia-2027-stay-olivers-deck.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5904b413-6768-4217-931e-4d385ac71898_compressed_fw-safari-asilia-2027-stay-olivers-pool.webp',
      ],
    },
    {
      name: 'The Highlands',
      nights: '2晚',
      detail: 'Olmoti高地，至火山口约1小时。',
      suggestedImage: 'camp-the-highlands.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/e6d67f1a-d1c3-4789-b688-0ac7435d5ddb_compressed_fw-safari-asilia-2027-stay-highlands-room.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/bd8b0d11-aabc-4875-9a95-56a1efd7b006_compressed_fw-safari-asilia-2027-stay-highlands-dome-exterior.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6dc643e4-4d86-4872-87d3-21bc776b8ee3_compressed_fw-safari-asilia-2027-stay-highlands-lounge.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/f9420209-88df-4ebd-97ce-a35434c8aa8b_compressed_fw-safari-asilia-2027-stay-highlands-hot-tub.webp',
      ],
    },
    {
      name: 'Sayari / Namiri Plains / Olakira 任选其一',
      nights: '3晚',
      detail: '第三段营地按季节三选一：Sayari Camp 靠近马拉河区域（迁徙季）、Namiri Plains 专注大猫生态、Olakira 随季节迁移。同一次行程只选其一。',
      suggestedImage: 'camp-serengei-third-night.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/afad2a65-5db6-4f0f-9dec-4905f9bfe72a_compressed_fw-safari-asilia-2027-stay-sayari-family-tent.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/c05f38e5-0072-4219-af6f-852ee7421c07_compressed_fw-safari-asilia-2027-stay-namiri-outdoor-bath.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/9be069ed-e5ee-4a63-8140-28ca92f1a011_compressed_fw-safari-asilia-2027-stay-olakira-stargazing-tent.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/7067357a-74d0-4bf4-bfc7-10a132dff8a7_compressed_fw-safari-asilia-2027-stay-olakira-milky-way.webp',
      ],
    },
  ],
  routeVersions: [],
  priceIntro:
    '2027参考价格，不代表房态；未列日期请咨询。',
  priceTables: [
    {
      name: 'Olakira',
      rows: [
        { date: '1/6–2/28', price: '10,525' },
        { date: '3/1–3/15', price: '8,735' },
        { date: '6/1–6/30', price: '10,660' },
        { date: '7/1–9/30', price: '11,900' },
        { date: '10/1–10/31', price: '11,036' },
        { date: '12/1–12/19', price: '9,196' },
        { date: '12/20–12/31', price: '11,900' },
      ],
      note: '未列日期请咨询。',
    },
    {
      name: 'Sayari',
      rows: [
        { date: '1/6–2/28', price: '9,145' },
        { date: '6/1–6/30', price: '10,510' },
        { date: '7/1–9/30', price: '14,200' },
        { date: '10/1–10/31', price: '13,346' },
        { date: '11/1–12/19', price: '9,050' },
        { date: '12/20–12/31', price: '14,200' },
      ],
      note: '未列日期请咨询。',
    },
    {
      name: 'Namiri',
      rows: [
        { date: '1/6–2/28', price: '12,800' },
        { date: '5/16–5/31', price: '7,115' },
        { date: '6/1–6/30', price: '14,170' },
        { date: '7/1–9/30', price: '14,200' },
        { date: '10/1–10/31', price: '10,886' },
        { date: '11/1–12/19', price: '10,246' },
        { date: '12/20–12/31', price: '14,200' },
      ],
      note: '未列日期请咨询。',
    },
  ],
  ctaTitle: '让顾问按季节匹配塞伦盖蒂营地',
  ctaButton: '咨询这条行程',
};

// ---------- 套餐二：Lemala ----------
const lemala: SafariPackageDetail = {
  path: '/safari-packages/tanzania/2027-lemala-safari',
  slug: '2027-lemala-safari',
  name: '2027 坦桑尼亚 Lemala 野奢 Safari',
  priceRange: 'USD 6,850 /人起',
  h1: '2027 坦桑尼亚 Lemala 野奢 Safari｜8天7晚',
  heroImage: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/48130819-c737-4545-a3a8-a66cf73bfdfd_compressed_fw-safari-lemala-2027-hero-tarangire-baobab.webp',
  heroSubtitle:
    '串联塔兰吉雷、恩戈罗恩戈罗与塞伦盖蒂的 Lemala 野奢 Safari 参考方案，中部或北部营地二选一。',
  seoTitle: '2027坦桑尼亚Lemala野奢Safari 8天7晚｜自由旷野',
  seoDescription:
    '2027坦桑尼亚8天7晚Lemala野奢Safari参考行程，串联塔兰吉雷、恩戈罗恩戈罗与塞伦盖蒂，可选中部Nanyukie或北部Kuria Hills，清楚展示逐日路线、内陆航班、共享游猎、分季参考价格与费用边界。',
  keywords: 'Lemala,坦桑尼亚Safari,塔兰吉雷,恩戈罗恩戈罗,塞伦盖蒂,Nanyukie,Kuria Hills,Mpingo Ridge,Osonjoi,8天7晚',
  tags: ['坦桑尼亚', '8天7晚', '乞力马扎罗机场往返', '2人起', '2027参考价', '不含国际机票'],
  overview: [
    { label: '目的地', value: '坦桑尼亚' },
    { label: '行程天数', value: '8天7晚' },
    { label: '进出机场', value: '乞力马扎罗机场往返' },
    { label: '出行人数', value: '2人起' },
    { label: '保护区', value: '塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂' },
    { label: '营地组合', value: 'Mpingo Ridge 2晚 + Osonjoi 2晚 + 塞伦盖蒂营地3晚' },
    { label: '塞伦盖蒂营地', value: 'Nanyukie 或 Kuria Hills 二选一' },
    { label: '交通方式', value: '内陆小飞机 + 营地接送' },
    { label: '游猎方式', value: '共享车辆和向导' },
    { label: '参考价格', value: '2027参考价' },
    { label: '不含项', value: '国际机票' },
  ],
  routeText: '乞力马扎罗机场 → 塔兰吉雷2晚 → 恩戈罗恩戈罗2晚 → 塞伦盖蒂中部或北部二选一3晚 → 乞力马扎罗机场',
  features: [
    '全程 Lemala 旗下营地，串联塔兰吉雷、恩戈罗恩戈罗、塞伦盖蒂',
    '塞伦盖蒂中部 Nanyukie 或北部 Kuria Hills 二选一',
    'Mpingo Ridge 客房设私人泳池，Osonjoi 位于火山口东部边缘',
    '共享车辆和向导，分季参考价格清晰呈现预算差异',
  ],
  highlights: [
    { title: '经典北部三站', description: '塔兰吉雷、火山口、塞伦盖蒂三站串联，覆盖坦桑尼亚北部最经典的自然层次。', icon: 'ri-route-line' },
    { title: '中部或北部二选一', description: '塞伦盖蒂最后一站可选中部 Nanyukie 或北部 Kuria Hills，按季节与偏好取舍。', icon: 'ri-arrow-left-right-line' },
    { title: '每站连住两晚', description: '每个营地连住两晚以上，节奏从容，不频繁换营。', icon: 'ri-hotel-line' },
    { title: '内陆交通衔接', description: '小飞机与营地接送衔接，减少长途车程。', icon: 'ri-plane-line' },
    { title: '住宿各有特点', description: 'Mpingo Ridge 客房设私人泳池，Osonjoi 靠近火山口东部边缘。', icon: 'ri-vip-crown-line' },
  ],
  plannerNote:
    '这条环线用“塔兰吉雷—火山口—塞伦盖蒂”三个最经典的区域，讲清楚坦桑尼亚北部的自然层次：稀树草原的大象、封闭火山口的五霸、无边草海的兽群。每个营地连住两晚，节奏从容；塞伦盖蒂最后一站提供中部与北部两种选择，中部更稳、北部更接近迁徙季的马拉河区域。小飞机与营地接送衔接，适合希望用8天7晚走完经典环线、又在意住宿品质的家庭与小团体。',
  mapStops: [
    { name: '乞力马扎罗机场', dayLabel: 'D1', coords: [-3.429, 37.074] },
    { name: '塔兰吉雷国家公园', dayLabel: 'D1-2', coords: [-3.6, 36.0] },
    { name: '恩戈罗恩戈罗保护区', dayLabel: 'D3-4', coords: [-3.2, 35.55] },
    { name: '塞伦盖蒂国家公园', dayLabel: 'D5-7', coords: [-2.3, 34.8] },
    { name: '乞力马扎罗机场', dayLabel: 'D8', coords: [-3.429, 37.074] },
  ],
  itinerary: [
    {
      day: '第1天',
      title: '落地塔兰吉雷，住进山脊上的私人泳池营地',
      location: '乞力马扎罗机场 接机，飞塔兰吉雷',
      arrangement: '内陆航班抵达，入住营地',
      accommodation: 'Mpingo Ridge',
      meals: '营地早餐 / 游猎午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/ce5d18ad-ba93-4399-b6d7-7823310cadf3_compressed_fw-safari-lemala-2027-d01-mpingo-aerial.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/91819343-eef3-4e86-8617-6b090437d445_compressed_fw-safari-lemala-2027-d01-mpingo-deck.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6902ba82-c623-402b-954d-913a35951009_compressed_fw-safari-lemala-2027-d01-mpingo-pool-view.webp',
      ],
      imageAlts: ['Mpingo Ridge 航拍全景', 'Mpingo Ridge 露台', 'Mpingo Ridge 私人泳池景观'],
      content:
        '乞力马扎罗机场接机后，乘内陆小飞机飞往塔兰吉雷。Mpingo Ridge 坐落在塔兰吉雷国家公园附近的山脊上，距 Kuro 简易机场约45分钟车程，每间客房都带私人泳池——刚落地就能在池边放松，远眺山脊下的稀树草原。今天以安顿为主，为第二天的全天游猎养精蓄锐。',
      activities: [
        { name: '乞力马扎罗机场 接机 + 内陆航班', icon: 'ri-flight-takeoff-line' },
        { name: '入住 Mpingo Ridge', icon: 'ri-hotel-line' },
      ],
    },
    {
      day: '第2天',
      title: '塔兰吉雷全天游猎，追踪大象与猴面包树',
      location: '塔兰吉雷国家公园',
      arrangement: '共享车辆 Safari 全天游猎',
      accommodation: 'Mpingo Ridge（2晚）',
      meals: '营地早餐 / 游猎野餐午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/ce5cb819-75b4-43c2-b36b-fc7035e8ac8a_compressed_fw-safari-lemala-2027-d02-tarangire-balloon.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/b28214c2-e5d0-47e7-a4f3-b928de220f09_compressed_fw-safari-lemala-2027-d02-tarangire-elephant-drive.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5e6e2ede-1dce-4504-a6b9-53096b658f88_compressed_fw-safari-lemala-2027-d02-tarangire-walk.webp',
      ],
      imageAlts: ['塔兰吉雷热气球俯瞰', '塔兰吉雷象群游猎', '塔兰吉雷徒步'],
      content:
        '塔兰吉雷是非洲大象密度最高的国家公园之一，猴面包树与象群是这里的名片。全天乘坐共享车辆游猎，追踪象群、狮群与其他野生动物。营地还提供多种可加购的体验：夜间游猎（2人起）、季节性徒步（14岁以上）、丛林早餐、部落参访与夕阳酒会，均可按需提前预订。',
      activities: [
        { name: '共享车辆全天游猎', icon: 'ri-compass-3-line' },
        { name: '夜间游猎（另付费，2人起）', icon: 'ri-moon-clear-line' },
        { name: '季节性徒步（另付费，14岁以上）', icon: 'ri-footprint-line' },
        { name: '丛林早餐 / 部落参访 / 夕阳酒会（另付费）', icon: 'ri-restaurant-2-line' },
      ],
    },
    {
      day: '第3天',
      title: '飞曼雅拉，翻山登火山口东部边缘',
      location: '飞曼雅拉，转车至火山口',
      arrangement: '内陆航班 + 车行约1.5–2小时',
      accommodation: 'Osonjoi',
      meals: '营地早餐 / 营地午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5362e047-6581-4d57-8c77-5a824d014e17_compressed_fw-safari-lemala-2027-d03-osonjoi-lodge-exterior.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/44b16193-23ad-4176-b50a-7005e430f596_compressed_fw-safari-lemala-2027-d03-osonjoi-lounge.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/4b1e359e-0232-4402-9055-3dcb8125e028_compressed_fw-safari-lemala-2027-d03-osonjoi-suites.webp',
      ],
      imageAlts: ['Osonjoi 营地外观', 'Osonjoi 休息区', 'Osonjoi 套房'],
      content:
        '早餐后飞抵曼雅拉，换乘越野车沿山路向火山口行进约1.5–2小时。Osonjoi 坐落在恩戈罗恩戈罗火山口东部边缘，距离火山口底部仅约15分钟车程，是前往世界最大火山口最近、最方便的营地之一。傍晚在营地安顿，俯瞰山脊下的壮阔风景。',
      activities: [
        { name: '内陆航班', icon: 'ri-plane-line' },
        { name: '车行约1.5–2小时', icon: 'ri-car-line' },
        { name: '入住 Osonjoi', icon: 'ri-hotel-line' },
      ],
    },
    {
      day: '第4天',
      title: '下行世界最大火山口，追踪非洲五霸',
      location: '恩戈罗恩戈罗火山口',
      arrangement: '火山口 Safari + 野餐午餐',
      accommodation: 'Osonjoi（2晚）',
      meals: '营地早餐 / 火山口野餐午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/62f9a69e-2467-43fd-be81-842e796e894d_compressed_fw-safari-lemala-2027-d04-ngorongoro-rhino.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/efcadd2b-104b-419d-bfb8-36850feb62de_compressed_fw-safari-lemala-2027-d04-ngorongoro-wildebeest.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/629843c8-2e23-4ed9-9050-640fd673425b_compressed_fw-safari-lemala-2027-d04-osonjoi-lions.webp',
      ],
      imageAlts: ['恩戈罗恩戈罗黑犀牛', '恩戈罗恩戈罗角马', '恩戈罗恩戈罗狮群'],
      content:
        '驾车下行进入恩戈罗恩戈罗火山口，这片260平方公里的封闭盆地是非洲最稳定的黑犀牛栖息地之一，狮群、非洲象、非洲水牛、花豹也常在此同框出现。全天火山口游猎，午餐在火山口底部野餐享用。营地另提供火山口热食午餐、马赛村参访、自然漫步与私人夕阳酒会等加购体验。所有动物均为野生，无法承诺出现。',
      activities: [
        { name: '火山口全天游猎', icon: 'ri-landscape-line' },
        { name: '火山口野餐午餐', icon: 'ri-restaurant-2-line' },
        { name: '火山口热食午餐 / 马赛村 / 自然漫步 / 夕阳酒会（另付费）', icon: 'ri-footprint-line' },
      ],
    },
    {
      day: '第5天',
      title: '飞越无边草原，抵达塞伦盖蒂中部或北部',
      location: '飞塞伦盖蒂中部或北部',
      arrangement: '内陆航班抵达，入住营地',
      accommodation: 'Nanyukie 或 Kuria Hills',
      meals: '营地早餐 / 游猎午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/c2c80e23-0cf4-4b80-a8da-9628faa4f122_compressed_fw-safari-lemala-2027-d05-kuria-leopard.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/d0271b3b-5af2-455b-a8de-ae61b13ee061_compressed_fw-safari-lemala-2027-d05-nanyukie-game-drive.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/bbf11281-892b-4eb9-9c64-273e8676ec3d_compressed_fw-safari-lemala-2027-d05-nanyukie-sunset.webp',
      ],
      imageAlts: ['Kuria Hills 花豹', 'Nanyukie 游猎车程', 'Nanyukie 日落'],
      content:
        '乘小飞机飞抵塞伦盖蒂。按季节与偏好，最后一站可在中部 Nanyukie 与北部 Kuria Hills 之间二选一：中部更稳、常年动物分布较均衡；北部更靠近迁徙季的马拉河区域。抵达营地后稍作安顿，为接下来两整天的草原游猎做好准备。',
      activities: [
        { name: '内陆航班', icon: 'ri-plane-line' },
        { name: '营地接送', icon: 'ri-car-line' },
        { name: '入住 Nanyukie 或 Kuria Hills', icon: 'ri-hotel-line' },
      ],
    },
    {
      day: '第6–7天',
      title: '两整天扎进塞伦盖蒂，中部或北部各有精彩',
      location: '塞伦盖蒂两整天游猎',
      arrangement: '共享 Safari 全天游猎',
      accommodation: 'Nanyukie 或 Kuria Hills（3晚）',
      meals: '营地早餐 / 游猎野餐午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/74a70e8e-d54b-41c0-bd2c-468a1f643802_compressed_fw-safari-lemala-2027-d06-07-kuria-balloon.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/f84cf727-de79-40aa-be28-f7149b57e1ba_compressed_fw-safari-lemala-2027-d06-07-kuria-migration.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/f9e41951-70f6-4d96-803d-58f2fcf283c7_compressed_fw-safari-lemala-2027-d06-07-kuria-cheetah.webp',
      ],
      imageAlts: ['Kuria Hills 热气球', '塞伦盖蒂角马迁徙', 'Kuria Hills 猎豹'],
      content:
        '连续两整天乘坐共享车辆深入塞伦盖蒂草原，追踪兽群与大猫。选择中部 Nanyukie，草原开阔、动物分布较均衡；选择北部 Kuria Hills，更靠近迁徙季兽群北上的马拉河区域。两处营地均可加购徒步、热气球等体验，Kuria Hills 另可安排丛林早餐与私人夕阳酒会。动物迁徙与渡河均为自然现象，无法保证。',
      activities: [
        { name: '共享车辆全天游猎', icon: 'ri-compass-3-line' },
        { name: '徒步 / 热气球（另付费）', icon: 'ri-footprint-line' },
        { name: '丛林早餐 / 私人夕阳酒会（另付费）', icon: 'ri-restaurant-2-line' },
      ],
    },
    {
      day: '第8天',
      title: '最后一个草原清晨，飞回乞力马扎罗',
      location: '飞回乞力马扎罗机场',
      arrangement: '内陆航班返程',
      accommodation: '—',
      meals: '营地早餐 / 午餐盒或机场简餐 / 自理',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6057d478-68ea-43eb-87a8-17083a76a690_compressed_fw-safari-lemala-2027-d08-kuria-silhouette.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/b755b409-9bf3-4e68-bae4-7908b4f353a7_compressed_fw-safari-lemala-2027-d08-kuria-sundowner.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/97be2b8a-a798-413a-a724-07e77cbdafb4_compressed_fw-safari-lemala-2027-d08-kuria-terrace.webp',
      ],
      imageAlts: ['草原剪影', 'Kuria Hills 夕阳酒会', 'Kuria Hills 露台'],
      content:
        '清晨在营地享用早餐后，乘内陆小飞机飞回乞力马扎罗机场，为衔接国际航班预留充足时间。8天7晚的坦桑尼亚北部野奢之旅到此结束，关于旷野的记忆才刚刚开始沉淀。',
      activities: [
        { name: '内陆航班返程', icon: 'ri-plane-line' },
        { name: '国际航班衔接', icon: 'ri-flight-takeoff-line' },
      ],
    },
  ],
  camps: [
    {
      name: 'Mpingo Ridge',
      nights: '2晚',
      detail: '距Kuro约45分钟，客房设私人泳池。',
      suggestedImage: 'camp-mpingo-ridge.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5ed0c79b-427d-4d7f-8cbd-166cbf2d8434_compressed_fw-safari-lemala-2027-stay-mpingo-exterior.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/2370b2df-9157-49c3-8876-2a0c750d2a76_compressed_fw-safari-lemala-2027-stay-mpingo-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/fa32815d-4320-44e9-8fab-0d18c4fa5576_compressed_fw-safari-lemala-2027-stay-mpingo-pool.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/16e2dc10-b4e1-473d-aa17-90b3e882f127_compressed_fw-safari-lemala-2027-stay-mpingo-lounge.webp',
      ],
    },
    {
      name: 'Osonjoi',
      nights: '2晚',
      detail: '火山口东部边缘，至火山口底部约15分钟。',
      suggestedImage: 'camp-osonjoi.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/fcca1494-9491-4fb0-a8ce-2a6c639e4df7_compressed_fw-safari-lemala-2027-stay-osonjoi-main-area.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/74931139-5e7d-4688-9ecd-5cabc4101158_compressed_fw-safari-lemala-2027-stay-osonjoi-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/7f9ecea3-f141-4cc5-b954-9eca3447e825_compressed_fw-safari-lemala-2027-stay-osonjoi-dining.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/0bb60b9b-e0db-4d74-bb71-1bfc9232ae49_compressed_fw-safari-lemala-2027-stay-osonjoi-bathroom.webp',
      ],
    },
    {
      name: 'Nanyukie 或 Kuria Hills',
      nights: '3晚',
      detail: '第三段营地二选一：Nanyukie 位于塞伦盖蒂中部，Kuria Hills 位于北部。同一次行程只选其一。',
      suggestedImage: 'camp-serengei-nanyukie-kuria.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/88ae9855-63d1-449e-8c73-01702a60ee29_compressed_fw-safari-lemala-2027-stay-nanyukie-pool.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/9b477daf-4ee1-4c68-9841-e57db23cd945_compressed_fw-safari-lemala-2027-stay-kuria-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/e83b90ff-184f-4f83-99c9-012a687f3c2f_compressed_fw-safari-lemala-2027-stay-kuria-pool.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/e7ffd63a-4988-4c3f-a9f5-19799560fd91_compressed_fw-safari-lemala-2027-stay-nanyukie-bedroom.webp',
      ],
    },
  ],
  routeVersions: [],
  priceIntro: '2027参考价格，不代表房态；未列日期请咨询。',
  priceTables: [
    {
      name: 'Nanyukie',
      rows: [
        { date: '1/6–2/28', price: '9,680' },
        { date: '3/1–5/31', price: '6,850' },
        { date: '6/1–6/30', price: '13,000' },
        { date: '7/1–9/30', price: '16,500' },
        { date: '10/1–12/19', price: '9,020' },
        { date: '12/20–2028/1/5', price: '16,460' },
      ],
    },
    {
      name: 'Kuria Hills',
      rows: [
        { date: '1/6–2/28', price: '9,150' },
        { date: '3/1–5/31', price: '请咨询' },
        { date: '6/1–6/30', price: '11,750' },
        { date: '7/1–9/30', price: '16,780' },
        { date: '10/1–10/31', price: '9,360' },
        { date: '11/1–12/19', price: '8,280' },
        { date: '12/20–2028/1/5', price: '16,730' },
      ],
    },
  ],
  ctaTitle: '中部还是北部？先从出行月份开始匹配',
  ctaButton: '咨询这条行程',
};

// ---------- 套餐三：东非 andBeyond ----------
const andBeyond: SafariPackageDetail = {
  path: '/safari-packages/east-africa/2026-andbeyond-safari',
  slug: '2026-andbeyond-safari',
  name: '2026 东非 8天7晚 野奢游猎',
  h1: '2026 东非 8天7晚 野奢游猎｜肯尼亚与坦桑尼亚连线参考行程',
  heroImage: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6db00438-887c-40da-b4ed-54de30898ec7_compressed_fw-safari-east-africa-2026-hero-bateleur-sunset-drive.webp',
  heroSubtitle:
    '从内罗毕进入马赛马拉，跨境深入塞伦盖蒂与恩戈罗恩戈罗，以 andBeyond 营地为核心的肯尼亚与坦桑尼亚连线参考方案。',
  seoTitle: '2026肯尼亚坦桑尼亚连线Safari 8天7晚｜自由旷野',
  seoDescription:
    '2026东非8天7晚肯尼亚与坦桑尼亚连线Safari参考行程，从内罗毕进入马赛马拉Bateleur Camp，跨境深入塞伦盖蒂与恩戈罗恩戈罗火山口，以andBeyond营地为核心，展示逐日路线、营地组合、内陆交通与费用边界。',
  keywords: 'andBeyond,肯尼亚,坦桑尼亚,马赛马拉,塞伦盖蒂,恩戈罗恩戈罗,Bateleur Camp,8天7晚,野奢Safari',
  tags: ['肯尼亚 + 坦桑尼亚连线', '8天7晚', '3家营地共7晚', '开口行程', '价格请咨询', '不含国际机票'],
  overview: [
    { label: '目的地', value: '肯尼亚 + 坦桑尼亚连线' },
    { label: '行程天数', value: '8天7晚' },
    { label: '营地结构', value: '3家营地共7晚、每家至少2晚' },
    { label: '进出机场', value: '内罗毕进 / 乞力马扎罗机场出（开口行程）' },
    { label: '出行人数', value: '单人可询价，常规按2人及以上，超过12人重新计价' },
    { label: '交通方式', value: '内陆小飞机 + 营地接送' },
    { label: '车辆与向导', value: '以最终确认单为准' },
    { label: '参考价格', value: '请咨询' },
    { label: '不含项', value: '国际机票' },
  ],
  routeText: '内罗毕机场 → 马赛马拉3晚 → 塞伦盖蒂2晚 → 恩戈罗恩戈罗火山口2晚 → 乞力马扎罗机场',
  features: [
    '从肯尼亚马赛马拉跨境进入坦桑尼亚，一条线路串联两国经典',
    '以 andBeyond 营地为核心，3家营地共7晚、每家至少2晚',
    '马赛马拉 Bateleur Camp 3晚 → 塞伦盖蒂三选一 2晚 → 火山口二选一 2晚',
    '内陆小飞机 + 营地接送，开口路线进出，车辆和向导以最终确认单为准',
  ],
  highlights: [
    { title: '两国连线一次走完', description: '从肯尼亚马赛马拉到坦桑尼亚塞伦盖蒂与火山口，一条线路串联两国经典。', icon: 'ri-earth-line' },
    { title: '3家营地共7晚', description: '每家营地至少两晚，避免频繁换营，行程节奏更从容。', icon: 'ri-hotel-line' },
    { title: '马赛马拉开场', description: '先在马赛马拉 Bateleur Camp 连住3晚，追踪大猫与迁徙，再跨境深入坦桑尼亚。', icon: 'ri-compass-3-line' },
    { title: '内陆小飞机衔接', description: '长距离移动由内陆小飞机完成，车辆与向导以最终确认单为准。', icon: 'ri-plane-line' },
  ],
  plannerNote:
    '这条连线把肯尼亚的马赛马拉与坦桑尼亚的塞伦盖蒂、恩戈罗恩戈罗火山口串成一条开口路线：内罗毕进、乞力马扎罗出，不用折返。先在马赛马拉连住3晚追踪大猫与迁徙，再跨境进入塞伦盖蒂2晚，最后登上世界最大火山口2晚，节奏由开阔草原逐步收拢到封闭盆地。全程3家营地、每家至少2晚，避免频繁换营；内陆小飞机与营地接送衔接，车辆与向导以最终确认单为准。适合希望用8天7晚一次性走完肯尼亚与坦桑尼亚两国经典的旅行者。',
  mapStops: [
    { name: '内罗毕机场', dayLabel: 'D1', coords: [-1.2921, 36.8219] },
    { name: '马赛马拉', dayLabel: 'D1-3', coords: [-1.5, 35.14] },
    { name: '塞伦盖蒂国家公园', dayLabel: 'D4-5', coords: [-2.3, 34.8] },
    { name: '恩戈罗恩戈罗火山口', dayLabel: 'D6-7', coords: [-3.2, 35.55] },
    { name: '乞力马扎罗机场', dayLabel: 'D8', coords: [-3.429, 37.074] },
  ],
  campFacts: [
    'Bateleur 可安排越野车和夜游，热气球等另确认。',
    'Serengeti Under Canvas 随季节迁移、最低6岁。',
    'Klein\'s Camp 位于特许保护区。',
    'Grumeti River Lodge 位于河流附近、4月关闭。',
    'Entamanu 最低入住年龄5岁。',
    'Osonjoi 最低入住年龄4岁。',
  ],
  itinerary: [
    {
      day: '第1–3天',
      title: '内罗毕落地，进入马赛马拉住进 Bateleur Camp',
      location: '马赛马拉国家保护区（肯尼亚）',
      arrangement: '内罗毕机场接机 + 内陆航班抵达，每日游猎',
      accommodation: 'Bateleur Camp（3晚）',
      meals: '营地早餐 / 游猎野餐午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/0491fbf1-ab0a-44cc-a4a0-fa423d263b6c_compressed_fw-safari-east-africa-2026-d01-03-maasai-visit.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/02d6c386-9011-48f2-9448-2a97dd97948b_compressed_fw-safari-east-africa-2026-d01-03-mara-balloon.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/3533bda0-36f7-4ea7-babe-5dd21d9f657c_compressed_fw-safari-east-africa-2026-d01-03-mara-elephants.webp',
      ],
      imageAlts: ['马赛马拉马赛村参访', '马赛马拉热气球日出', '马赛马拉象群'],
      content:
        '内罗毕机场接机后，乘内陆小飞机飞往马赛马拉。Bateleur Camp 位于马赛马拉，以开阔草原与密集的大猫、迁徙兽群著称。三天三夜里，向导每天带你出车：清晨追踪狮群与猎豹，黄昏跟随角马与斑马群，7–10月的迁徙季更有可能见证兽群渡河（自然现象，无法保证）。营地可安排越野车和夜游，热气球与马赛村参访等体验需另行确认。',
      activities: [
        { name: '内罗毕机场 接机 + 内陆航班', icon: 'ri-flight-takeoff-line' },
        { name: '每日游猎出车', icon: 'ri-compass-3-line' },
        { name: '热气球日出（另订）', icon: 'ri-sun-line' },
        { name: '马赛村参访（另订）', icon: 'ri-community-line' },
      ],
    },
    {
      day: '第4–5天',
      title: '跨境进入塞伦盖蒂，住进草原上的营地',
      location: '塞伦盖蒂国家公园（坦桑尼亚）',
      arrangement: '跨境航班 + 全天游猎',
      accommodation: 'Grumeti River Lodge / Klein\'s Camp / Serengeti Under Canvas 三选一（2晚）',
      meals: '营地早餐 / 游猎野餐午餐盒 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/4e4cc594-93ea-4a88-9944-70b424bf08ef_compressed_fw-safari-east-africa-2026-d04-05-grumeti-lioness-cubs.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/ccf28c06-e9eb-4f87-bc4b-216fb2e79a2a_compressed_fw-safari-east-africa-2026-d04-05-kleins-antelope-panorama.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/746171a4-e4c3-476d-a64d-c14707aa2720_compressed_fw-safari-east-africa-2026-d04-05-under-canvas-migration.webp',
      ],
      imageAlts: ['Grumeti 母狮与幼崽', 'Klein’s Camp 羚羊全景', 'Under Canvas 迁徙兽群'],
      content:
        '从马赛马拉跨境进入坦桑尼亚的塞伦盖蒂。这一站按季节与偏好三选一：Grumeti River Lodge 位于河流附近（4月关闭）、Klein\'s Camp 位于特许保护区、Serengeti Under Canvas 随季节迁移、最低入住年龄6岁。两整天全天游猎，追踪兽群与大猫。所有动物均为野生，无法承诺出现。',
      activities: [
        { name: '跨境航班', icon: 'ri-plane-line' },
        { name: '全天游猎', icon: 'ri-compass-3-line' },
      ],
    },
    {
      day: '第6–7天',
      title: '登火山口，住西缘或东缘营地追踪五霸',
      location: '恩戈罗恩戈罗火山口（坦桑尼亚）',
      arrangement: '车行转场 + 火山口全天游猎',
      accommodation: 'Nomad Entamanu 或 Lemala Osonjoi（2晚）',
      meals: '营地早餐 / 火山口野餐午餐 / 营地晚餐',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/c479c641-cf0f-4a34-b616-e0e4e58c1d96_compressed_fw-safari-east-africa-2026-d06-07-entamanu-view.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6dd3cadd-3582-4667-acca-21a30e4e7b24_compressed_fw-safari-east-africa-2026-d06-07-ngorongoro-elephant.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/209f5d3c-56be-4339-a36f-b628b39f8b9c_compressed_2fw-safari-east-africa-2026-d06-07-ngorongoro-rhino.webp',
      ],
      imageAlts: ['Nomad Entamanu 火山口景观', '恩戈罗恩戈罗象群', '恩戈罗恩戈罗黑犀牛'],
      content:
        '前往恩戈罗恩戈罗火山口，这一站可在 Nomad Entamanu（火山口西缘，最低入住年龄5岁）与 Lemala Osonjoi（火山口东部边缘，最低入住年龄4岁）之间二选一。下行进入世界最大火山口，全天游猎——这里是非洲最稳定的黑犀牛栖息地之一，狮群、非洲象、非洲水牛、花豹常同框出现。午餐在火山口底部野餐享用。所有动物均为野生，无法承诺出现。',
      activities: [
        { name: '车行转场', icon: 'ri-car-line' },
        { name: '火山口全天游猎', icon: 'ri-landscape-line' },
        { name: '火山口野餐午餐', icon: 'ri-restaurant-2-line' },
      ],
    },
    {
      day: '第8天',
      title: '飞回乞力马扎罗，结束两国连线之旅',
      location: '飞回乞力马扎罗机场',
      arrangement: '内陆航班返程，衔接国际航班',
      accommodation: '—',
      meals: '营地早餐 / 午餐盒或机场简餐 / 自理',
      images: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/fbfc2129-5dd8-4747-91fb-7fa40189349e_compressed_fw-safari-east-africa-2026-d08-crater-rim-walk.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/d50ca89c-688e-4d4c-b8f3-22cd7bc1ff0d_compressed_fw-safari-east-africa-2026-d08-entamanu-breakfast-view.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/3871778c-34b3-4678-8363-eaa4ff828796_compressed_fw-safari-east-africa-2026-d08-ngorongoro-giraffe.webp',
      ],
      imageAlts: ['恩戈罗恩戈罗火山口边缘徒步', 'Entamanu 早餐景观', '恩戈罗恩戈罗长颈鹿'],
      content:
        '清晨在营地享用早餐后，乘内陆小飞机飞回乞力马扎罗机场，为国际航班衔接预留充足时间。这条肯尼亚与坦桑尼亚连线行程到此结束，关于旷野的记忆才刚刚开始沉淀。',
      activities: [
        { name: '内陆航班返程', icon: 'ri-plane-line' },
        { name: '国际航班衔接', icon: 'ri-flight-takeoff-line' },
      ],
    },
  ],
  camps: [
    {
      name: 'Bateleur Camp',
      nights: '3晚',
      detail: '马赛马拉（肯尼亚），追踪大猫与迁徙。',
      suggestedImage: 'camp-bateleur.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/5386549d-8169-4935-adda-09a1233dfac8_compressed_fw-safari-east-africa-2026-stay-bateleur-pool.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6c3f5fa0-9b8d-4b40-85b0-ec88efcf0e73_compressed_fw-safari-east-africa-2026-stay-bateleur-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/f0bdaeb9-e6c6-48b8-b5b4-0819708dccab_compressed_fw-safari-east-africa-2026-stay-bateleur-dining.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/1544aa44-295e-4cec-8b1e-b33e0cdeb9c5_compressed_fw-safari-east-africa-2026-stay-bateleur-lounge.webp',
      ],
    },
    {
      name: 'Grumeti River Lodge / Klein\'s Camp / Serengeti Under Canvas 任选其一',
      nights: '2晚',
      detail: '塞伦盖蒂区域三选一：Grumeti River Lodge 位于河流附近、4月关闭；Klein\'s Camp 位于特许保护区；Serengeti Under Canvas 随季节迁移、最低入住年龄6岁。',
      suggestedImage: 'camp-grumeti-kleins-under-canvas.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/353fbaf6-034c-4281-8a60-cf4be059c9d7_compressed_fw-safari-east-africa-2026-stay-under-canvas-exterior.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/359c0416-18a1-454e-9432-43b92e4bf745_compressed_fw-safari-east-africa-2026-stay-grumeti-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/79ccc7a6-d3e3-4076-86fe-2ac7cf36998a_compressed_fw-safari-east-africa-2026-stay-kleins-cottage.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6e3f2364-cfdd-4987-85c8-61057df60c9d_compressed_fw-safari-east-africa-2026-stay-under-canvas-bedroom.webp',
      ],
    },
    {
      name: 'Nomad Entamanu 或 Lemala Osonjoi',
      nights: '2晚',
      detail: '火山口区域二选一：Entamanu 位于火山口西缘、最低入住年龄5岁；Lemala Osonjoi 位于火山口东部边缘、最低入住年龄4岁。',
      suggestedImage: 'camp-nomad-entamanu-osonjoi.jpg',
      image: 'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/93c6bdad-7111-4dc9-a093-9a3290d59d5a_compressed_fw-safari-east-africa-2026-stay-entamanu-mess-exterior.webp',
      gallery: [
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/1c9c5e8d-7453-4ea8-ae07-79d4d95495fe_compressed_fw-safari-east-africa-2026-stay-entamanu-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/4fec46b0-de8f-482d-bb27-897703e03013_compressed_fw-safari-east-africa-2026-stay-osonjoi-bedroom.webp',
        'https://storage.helloreaddy.io/project_files/8a4257b0-b93a-4ea5-a25c-adeacbcec648/6c60ddf0-8f1e-41b0-a29f-cda1316280c1_compressed_fw-safari-east-africa-2026-stay-osonjoi-outdoor-dining.webp',
      ],
    },
  ],
  routeVersions: [],
  consultationPriceNote:
    '参考价格请咨询。日期、营地组合、儿童年龄、跨国航线、内陆机票及车辆形式都会影响价格，需要重新核价。',
  ctaTitle: '一次走完肯尼亚与坦桑尼亚两国经典',
  ctaButton: '获取最终报价',
};

export const safariPackageDetails: SafariPackageDetail[] = [allAsilia, lemala, andBeyond];

export function getSafariPackageByPath(path: string): SafariPackageDetail | undefined {
  const normalized = path.endsWith('/') ? path.slice(0, -1) : path;
  return safariPackageDetails.find((p) => p.path === normalized);
}